import { onBeforeUnmount, ref, watch } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import createDeferred from "@/lib/deferredPromise";
import emitter from "@/lib/emitter";
import { EVENTS } from "@/const";

export const useMessageThreadsUpdate = (loader: (ids: string[]) => Promise<void>) => {
  const threadIdsToUpdate = ref<string[]>([]);
  const threadUpdating = ref(Promise.resolve());

  const onMessageHandler = (message?: Message) => {
    if (!message) return;
    threadIdsToUpdate.value.push(message.threadId);
  };

  const updateThread = (thread?: MessageThread) => {
    if (!thread) return;
    threadIdsToUpdate.value.push(thread.id);
  };

  const getUpdatedThreads = async (cb?: () => void) => {
    const ids = threadIdsToUpdate.value;
    threadIdsToUpdate.value = [];
    await loader(ids);
    cb && cb();
  };

  const onMessage = debounce(getUpdatedThreads, 3000, { leading: true });

  watch(threadIdsToUpdate, () => {
    if (!threadIdsToUpdate.value.length) return;
    const { promise, resolve } = createDeferred<void>();
    threadUpdating.value = promise;
    onMessage(resolve);
  });

  emitter.on<Message>(EVENTS.MESSAGES_NEW_MESSAGE, onMessageHandler);
  emitter.on<MessageThread>(EVENTS.MESSAGES_UPDATE_THREAD, updateThread);

  onBeforeUnmount(() => {
    emitter.off<Message>(EVENTS.MESSAGES_NEW_MESSAGE, onMessageHandler);
    emitter.off<MessageThread>(EVENTS.MESSAGES_UPDATE_THREAD, updateThread);
  });

  return {
    threadUpdating,
  };
};
